import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Loader2, ArrowRight, Home } from "lucide-react";
import { api } from "@/lib/api";
import { PrimaryLogo } from "@/components/Brand";
import { toast } from "sonner";

export default function AdminSetup() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/setup-status").then(({ data }) => {
      if (!data.needs_setup) {
        navigate("/admin/login?setup=done", { replace: true });
        return;
      }
      setChecking(false);
    }).catch(() => setChecking(false));
  }, [navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    if (pw.length < 12) { toast.error("Password must be at least 12 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/admin/setup", {
        name: name.trim(),
        email: email.trim(),
        password: pw,
        confirm_password: pw2,
      });
      toast.success("Administrator account created. The demo account has been removed.");
      navigate("/admin/login?setup=success", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete setup.");
    } finally { setBusy(false); }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F4F8FB" }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#0B2D59" }} />
      </div>
    );
  }

  const pwLenOk = pw.length >= 12;
  const pwMatch = pw && pw === pw2;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "#F4F8FB" }}>
      <div className="max-w-md w-full">
        <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm" style={{ color: "#0B2D59" }}>
          <Home className="w-4 h-4" /> Back to home
        </Link>
        <div className="flex justify-center mb-6">
          <PrimaryLogo maxWidth={240} />
        </div>
        <div className="p2p-card p-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-6 h-6" style={{ color: "#0B2D59" }} />
            <h1 className="font-serif text-2xl" style={{ color: "#0B2D59" }}>Create administrator account</h1>
          </div>
          <p className="text-sm" style={{ color: "#475569" }}>
            One-time setup. After you create your account, this page will be closed permanently and the demo account will be deleted.
          </p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <label htmlFor="setup-name" className="p2p-label">Administrator name</label>
              <input id="setup-name" type="text" className="p2p-input" data-testid="setup-name"
                value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
            </div>
            <div>
              <label htmlFor="setup-email" className="p2p-label">Administrator email</label>
              <input id="setup-email" type="email" className="p2p-input" data-testid="setup-email"
                value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <label htmlFor="setup-pw" className="p2p-label">New password</label>
              <div className="relative">
                <input id="setup-pw" type={show ? "text" : "password"} className="p2p-input pr-12" data-testid="setup-password"
                  value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="new-password" minLength={12} />
                <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"}
                  data-testid="setup-toggle-show"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full"
                  style={{ color: "#475569" }}>
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-xs mt-1.5" style={{ color: pwLenOk ? "#00A7B5" : "#475569" }}>
                {pwLenOk ? "✓ " : "•  "}At least 12 characters ({pw.length}/12)
              </div>
            </div>
            <div>
              <label htmlFor="setup-pw2" className="p2p-label">Confirm password</label>
              <input id="setup-pw2" type={show ? "text" : "password"} className="p2p-input" data-testid="setup-password-confirm"
                value={pw2} onChange={(e) => setPw2(e.target.value)} required autoComplete="new-password" minLength={12} />
              {pw2 && (
                <div className="text-xs mt-1.5" style={{ color: pwMatch ? "#00A7B5" : "#B91C1C" }}>
                  {pwMatch ? "✓ Passwords match" : "✗ Passwords do not match"}
                </div>
              )}
            </div>
            <button type="submit" className="p2p-btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="setup-submit" disabled={busy || !pwLenOk || !pwMatch || !name.trim() || !email.trim()}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {busy ? "Creating…" : "Create administrator account"}
            </button>
            <p className="text-xs text-center" style={{ color: "#475569" }}>
              Your password is hashed with bcrypt and stored securely. It is never displayed or logged.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
